/**
 * Pausen-Editor für eine Maßnahme.
 *  - Standard-Pause (Minuten) gilt für alle Tage (formData.pause_minutes).
 *  - Optional pro Wochentag (formData.weekday_pauses = { mon: 60, ... }),
 *    überschreibt die Standard-Pause am jeweiligen Tag.
 * Die Pause wird in den Exporten (Vergabe-Monatsexport, Honorar-Auswertung,
 * Einsatzplan-Summen) von der Brutto-Einsatzzeit abgezogen.
 */
import React from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Coffee } from 'lucide-react';

const WEEKDAYS = [
  { key: 'mon', label: 'Mo' },
  { key: 'tue', label: 'Di' },
  { key: 'wed', label: 'Mi' },
  { key: 'thu', label: 'Do' },
  { key: 'fri', label: 'Fr' },
];

export const PauseEditor = ({ formData, setFormData }) => {
  const perDay =
    formData.weekday_pauses != null &&
    typeof formData.weekday_pauses === 'object' &&
    Object.keys(formData.weekday_pauses).length > 0;

  const togglePerDay = (checked) => {
    if (checked) {
      const base = formData.pause_minutes === '' || formData.pause_minutes == null ? '' : Number(formData.pause_minutes);
      const wp = {};
      WEEKDAYS.forEach(({ key }) => { wp[key] = base === '' ? '' : base; });
      setFormData({ ...formData, weekday_pauses: wp });
    } else {
      setFormData({ ...formData, weekday_pauses: null });
    }
  };

  const setDay = (key, val) => {
    const v = val === '' ? '' : Math.max(0, parseInt(val, 10) || 0);
    setFormData({ ...formData, weekday_pauses: { ...(formData.weekday_pauses || {}), [key]: v } });
  };

  return (
    <div className="border-t pt-4 mt-4" data-testid="pause-editor">
      <Label className="text-sm font-medium mb-3 flex items-center gap-2">
        <Coffee className="h-4 w-4" />
        Pause (wird in Stunden-Exporten abgezogen)
      </Label>

      {!perDay && (
        <div className="flex items-center gap-2">
          <Input
            type="number" min="0" step="5"
            value={formData.pause_minutes ?? ''}
            onChange={(e) => setFormData({ ...formData, pause_minutes: e.target.value })}
            placeholder="z. B. 45"
            className="w-32"
            data-testid="pause-minutes-input"
          />
          <span className="text-sm text-muted-foreground">Minuten / Tag</span>
        </div>
      )}

      {perDay && (
        <div className="grid grid-cols-5 gap-2">
          {WEEKDAYS.map(({ key, label }) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs text-muted-foreground">{label}</Label>
              <Input
                type="number" min="0" step="5"
                value={(formData.weekday_pauses?.[key] ?? '')}
                onChange={(e) => setDay(key, e.target.value)}
                placeholder="Min."
                data-testid={`pause-weekday-${key}`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <Switch checked={perDay} onCheckedChange={togglePerDay} id="pause-perday" data-testid="pause-perday-toggle" />
        <Label htmlFor="pause-perday" className="text-xs font-normal text-muted-foreground">Pause je Wochentag festlegen</Label>
      </div>
    </div>
  );
};
